/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import { getIntrospectionQuery, buildClientSchema, parse, validate } from 'graphql';

import { EndpointNotAvailableException, IncompatibleSchemaException } from './exceptions';
import allQueries from '../../queries';

const cacheKeyPrefix = 'cif:features';
const cacheVersion = 'v1';
const cache = sessionStorage;

const inflightIntrospectionQueries = {};

export default class SchemaChecker {
    constructor(instanceUrl, gqlClient, configName) {
        this.instanceUrl = instanceUrl;
        this.configName = configName;
        this.gqlClient = gqlClient;
        this.storageKey = `${cacheKeyPrefix}:${cacheVersion}:${instanceUrl}`;
    }

    async getFeatures() {
        try {
            let data = this.getFeaturesFromCache();

            // validate if all expected features are available in the cached data, if not force a refetch
            if (data !== null) {
                const { compatibleVersion } = data;

                if (typeof compatibleVersion === 'undefined') {
                    data = null;
                }
            }

            // If features not available in storage, fetch it the schema
            if (data === null) {
                const result = await this.querySchema();
                const schema = buildClientSchema(result.data);

                // check features and construct features object
                data = {
                    compatibleVersion: this.checkCompatibleVersion(schema)
                };

                if (cache) {
                    try {
                        cache.setItem(this.storageKey, JSON.stringify(data));
                    } catch (err) {
                        console.warn('Cannot store features in cache', err);
                    }
                }
            }

            return data;
        } catch (err) {
            // Summarize all kind of Apollo exceptions
            return Promise.reject(new EndpointNotAvailableException(this.configName, err.message));
        }
    }

    /* private */ getFeaturesFromCache() {
        // Try to get features from cache
        if (!cache) {
            return null;
        }

        let data = cache.getItem(this.storageKey);

        if (data !== null) {
            try {
                data = JSON.parse(data);
            } catch (err) {
                // If schema from storage is invalid, remove it
                console.warn('Cannot parse schema from storage', err);
                data = null;
            }
        }

        if (data === null) {
            // no data in the storage. this may happen when the value could not be parsed
            // or the version of the cached data is older. In both cases we clear all
            // the keys with the cacheKeyPrefix which are not equal to the current storage
            // key to preserve space
            for (let i = 0; i < cache.length; i++) {
                const key = cache.key(i);
                if (key !== this.storageKey && key.indexOf(cacheKeyPrefix) === 0) {
                    cache.removeItem(key);
                }
            }
        }

        return data;
    }

    /* private */ async querySchema() {
        // check if there is already an introspection query in flight for the current instanceUrl
        // this addresses cases where for example multiple picker fields get initialized
        // simultaniously but in different contexts (different webcomponents)
        const { instanceUrl } = this;

        if (!inflightIntrospectionQueries[instanceUrl]) {
            inflightIntrospectionQueries[instanceUrl] = this.gqlClient
                .query({ query: parse(getIntrospectionQuery()) })
                // remove the entry from the global map to free the resources
                .then(response => {
                    // remove the entry from the global map to free the resources
                    delete inflightIntrospectionQueries[instanceUrl];
                    return response;
                })
                .catch(err => {
                    delete inflightIntrospectionQueries[instanceUrl];
                    throw err;
                });
        }

        return inflightIntrospectionQueries[instanceUrl];
    }

    /* private */ checkCompatibleVersion(schema) {
        let compatibleVersion = null;
        const { instanceUrl, configName } = this;
        // Sort versions from old to new
        const sortedVersions = Object.keys(allQueries).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

        // Map with all queries corresponding to their validation errors.
        // This approach allows us to validate queries only once, in case they are used by multiple versions.
        let queryMap = new Map();
        for (const version of sortedVersions) {
            for (const queryName of Object.keys(allQueries[version])) {
                const query = allQueries[version][queryName];
                queryMap.set(query, []);
            }
        }

        // Validate all queries against schema and store the errors
        for (let query of queryMap.keys()) {
            queryMap.set(query, validate(schema, query));
        }

        // Go through all versions and find the highest compatible version
        for (const version of sortedVersions) {
            let valid = true;
            const queries = allQueries[version];

            for (const [name, query] of Object.entries(queries)) {
                const errors = queryMap.get(query);
                if (errors.length > 0) {
                    // eslint-disable-next-line no-console
                    console.debug(
                        instanceUrl,
                        name,
                        errors.map(e => e.message)
                    );
                    valid = false;
                    break;
                }
            }

            if (valid) {
                compatibleVersion = version;
            }
            // eslint-disable-next-line no-console
            console.debug(
                `Schema of config ${configName} is ${!valid ? 'not ' : ''}compatible with schema ${version}.`
            );
        }

        if (compatibleVersion === null) {
            throw new IncompatibleSchemaException(configName);
        }

        return compatibleVersion;
    }
}
